import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useMedia } from 'react-use';
import { SelectorCarretArrowDown } from '../icons';
import Portal from '../portal/Portal';
import PrimaryLoader from '../primaryLoader/PrimaryLoader';
import { LOCALE_OPTIONS, SELECTOR_TYPES, useCountrySelector } from './useCountrySelector';
import { useOutsideClick } from '@srcDS/hooks/useOutsideClick';
import styles from './CountrySelector.module.scss';

const CountrySelector = ({
  items = LOCALE_OPTIONS,
  isInverted = false,
  bordered = false,
  value = null,
  setValue = () => {},
  type = SELECTOR_TYPES.COMPACT,
}) => {
  const {
    toggleDropdown,
    handleItemClick,
    closeDropdown,
    selectedItem,
    isOpen,
    getCurrentOption,
    getPopupPosition,
    popupPosition,
    setPopupPosition,
    loading,
  } = useCountrySelector({ items, value, setValue });
  const { label: currenLabel, special: currentSpecial } = getCurrentOption();
  const isDesktop = useMedia('(min-width: 1024px)', false);
  const elementRef = useRef(null);

  useOutsideClick({ ref: elementRef, handler: closeDropdown });

  useEffect(() => {
    setPopupPosition(getPopupPosition(elementRef));
  }, [elementRef, isDesktop]);

  return (
    <div
      ref={elementRef}
      className={clsx(styles.countrySelector, {
        [styles.countrySelectorIsInverted]: isInverted,
        [styles.countrySelectorBordered]: bordered,
        [styles.fullsizeType]: type === SELECTOR_TYPES.FULLSIZE,
      })}
    >
      <div className={styles.countrySelectorHeader} onClick={toggleDropdown}>
        {selectedItem && (
          <>
            {currentSpecial && <span className={styles.countrySelectorIcon}>{currentSpecial}</span>}
            <span className={styles.countrySelectorLabel}>{currenLabel}</span>
          </>
        )}
        <div className={styles.countrySelectorArrow} style={{ alignSelf: 'center' }}>
          <SelectorCarretArrowDown
            height="16"
            width="16"
            style={isOpen ? { transform: 'rotate(180deg)', transition: 'all .3s' } : { transform: 'rotate(0)' }}
          />
        </div>
      </div>
      <div
        className={clsx(styles.countrySelectorBody, isOpen && styles.countrySelectorBodyOpen, styles[popupPosition])}
      >
        {items &&
          items?.map((item) => (
            <div
              key={item.value}
              className={clsx(
                styles.countrySelectorItem,
                item.value === selectedItem && styles.countrySelectorItemActive,
              )}
              onClick={() => handleItemClick(item.value)}
            >
              {item.special && <span className={styles.countrySelectorIcon}>{item.special}</span>}
              <span className={styles.countrySelectorLabel}>{item?.label}</span>
            </div>
          ))}
      </div>
      {loading && (
        <Portal>
          <PrimaryLoader mode="segment" />
        </Portal>
      )}
    </div>
  );
};

CountrySelector.propTypes = {
  isInverted: PropTypes.bool,
  bordered: PropTypes.bool,
  value: PropTypes.string,
  setValue: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      special: PropTypes.any,
    }),
  ),
};

export default CountrySelector;
